import PubSub from 'pubsub-js';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formattedDateTime } from '../../../../../utils/dateUtils';
import { isDefined } from '../../../../../utils/variables';
import { AssetManager } from '../../../../../viewer/asset-manager';
import { Dxf } from '../../../../../viewer/elements/dxf';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { CadVolumeBoundaryDxf } from '../../../../../viewer/tools/utils/cad-volume-boudary-dxf';
import { VolumeBoundaryPolygon } from '../../../../../viewer/tools/utils/volume-boundary-polygon';
import { VolumeCalculatorTool } from '../../../../../viewer/tools/volume-calculator-tool';
import { Button } from '../../../button/button';
import { DropdownSuggestion } from '../../../dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { List } from '../../../list/list';
import { CollapsableSection } from '../../../skyview/right-menu/components/collapsable-section';
import { CollapsableSectionGroup } from '../../../skyview/right-menu/components/collapsable-section-group';
import { OptionLabels } from '../../../skyview/right-menu/components/option-labels';
import { RightPanelHeader } from '../../../skyview/right-menu/components/right-menu-header';
import { RightPanelContent, RightPanelFooter } from '../../../skyview/right-menu/components/styles';
import { SkyviewContext } from '../../../skyview/skyview-state';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';
import { useAssetsStore } from '../../state/assets-store';
import { VolumeCalculationDataType } from '../report-tool-menu/helpers/types';
import { BoundaryBlock } from './boundary-block';
import {
  BoundaryCalculationType,
  volumeCalculationHelper,
  VolumeCalculationInfo,
  VolumeCalculationMethod,
  VolumeCalculationOption,
} from './volume-calculation-helper';
import { VolumeInfoBlock } from './volume-info-block';

export const VolumeCalculationTool = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState({
    methodSection: true,
    targetAreaSection: true,
    sourceAreaSection: true,
    delimitSection: false,
  });

  const [selectedAsset] = React.useState(AssetManager.instance.getSelected());
  const { assets } = useAssetsStore();

  const [sourceVolumeOptions, setSourceVolumeOptions] = React.useState<VolumeCalculationOption[]>();
  const [targetVolumeOptions, setTargetVolumeOptions] = React.useState<VolumeCalculationOption[]>();
  const [selectedSource, setSelectedSource] = React.useState<VolumeCalculationOption | undefined>();
  const [selectedSourceInfo, setSelectedSourceInfo] = React.useState<VolumeCalculationInfo>();
  const [selectedTargetInfo, setSelectedTargetInfo] = React.useState<VolumeCalculationInfo>();
  const [selectedTarget, setSelectedTarget] = React.useState<VolumeCalculationOption | undefined>();
  const [selectedBoundaryAsset, setSelectedBoundaryAsset] = React.useState<Dxf | Geometry>();
  const [calculationMethod, setCalculationMethod] =
    React.useState<VolumeCalculationMethod>('cell_grid');

  const [volumeName, setVolumeName] = React.useState(
    t('defaultName', {
      ns: 'skyviewVolumeCalculation',
      dateTime: formattedDateTime(new Date()),
      interpolation: {
        escapeValue: false,
      },
    }) as string,
  );
  const [boundaryCalculationType, setBoundaryCalculationType] =
    React.useState<BoundaryCalculationType>(BoundaryCalculationType.NONE);

  const [polygonVolumeIsValid, setPolygonVolumeIsValid] = React.useState(false);

  const { activeTool } = React.useContext(SkyviewContext);
  if (!(activeTool instanceof VolumeCalculatorTool)) {
    throw new Error('Active tool is not an instance of VolumeCalculatorTool');
  }

  useEffect(() => {
    const sourceOptions = volumeCalculationHelper.loadSourceDataOptions(setSelectedSource, assets);
    const targetOptions = volumeCalculationHelper.loadTargetDataOptions(assets);
    volumeCalculationHelper.validateUniqueness(sourceOptions);
    volumeCalculationHelper.validateUniqueness(targetOptions);
    setSourceVolumeOptions(sourceOptions);
    setTargetVolumeOptions(targetOptions);
  }, [assets]);

  /**
   * Subscribes / unsubscribes for polygon real time changes
   */
  useEffect(() => {
    const onPolygonChangedSubscription = PubSub.subscribe(
      'onVolumeBoundaryPolygonChanged',
      (topic: string, polygon: VolumeBoundaryPolygon) => {
        setPolygonVolumeIsValid(activeTool.hasValidBoundary);
      },
    );

    return () => {
      if (onPolygonChangedSubscription) {
        PubSub.unsubscribe(onPolygonChangedSubscription);
      }
    };
  }, [activeTool]);

  /**
   * Loads a selected asset as a target
   */
  useEffect(() => {
    if (selectedAsset?.assetId) {
      const selectedVolumeOption = volumeCalculationHelper.findVolumeCalculationOptionByAssetId(
        targetVolumeOptions,
        selectedAsset?.assetId,
      );

      void volumeCalculationHelper.loadVolumeCalculationOption(
        selectedVolumeOption,
        setSelectedTarget,
      );
    }
  }, [activeTool, selectedAsset?.assetId, targetVolumeOptions]);

  const startCalculation = async () => {
    let boundaryAsset;
    let boundaryType: VolumeCalculationDataType | undefined;

    // If there is a geometry boundary, store and use the position as offset.
    if (
      boundaryCalculationType === BoundaryCalculationType.AREA &&
      selectedBoundaryAsset instanceof Geometry
    ) {
      boundaryAsset = selectedBoundaryAsset.createArea(selectedBoundaryAsset.position);
      boundaryType = VolumeCalculationDataType.Area;
    }

    // If there is a cad boundary, do not set the position offset.
    if (
      boundaryCalculationType === BoundaryCalculationType.CAD &&
      selectedBoundaryAsset instanceof Dxf
    ) {
      const boundaryData = new CadVolumeBoundaryDxf(selectedBoundaryAsset);
      boundaryAsset = boundaryData.extractBoundaryGeometry();
      boundaryType = VolumeCalculationDataType.Dxf;
    }

    PubSub.publish('start_volume_calculation', {
      volumeName: volumeName,
      targetData:
        selectedTargetInfo && selectedTarget
          ? {
              meshes: selectedTargetInfo.meshes,
              asset: selectedTarget.asset,
            }
          : undefined,
      sourceData:
        selectedSource && (selectedSourceInfo?.meshes || selectedSource?.pointCloudId)
          ? {
              meshes: selectedSourceInfo?.meshes,
              asset: selectedSource.asset!,
              pointCloudId: selectedSource?.pointCloudId,
              sourceTerrainModelGeoDataId: selectedSource?.sourceTerrainModelGeoDataId,
            }
          : undefined,
      boundaryData: boundaryAsset && { mesh: boundaryAsset, boundaryType },
      method: calculationMethod,
    });

    AssetManager.instance.select(selectedTarget?.asset);
    await selectedTarget?.asset?.onChange();
  };

  const isNameValid = isDefined(volumeName) && volumeName.length > 3;
  const areAssetsSame = volumeCalculationHelper.checks.areVolumeCalculationAssetsTheSame(
    selectedTarget,
    selectedSource,
  );
  const isSourceAssetValid = volumeCalculationHelper.checks.isVolumeCalculationDataValid(
    selectedSource,
    selectedSourceInfo,
  );
  const isTargetAssetValid = volumeCalculationHelper.checks.isVolumeCalculationDataValid(
    selectedTarget,
    selectedTargetInfo,
  );
  const isPrismVolumeTerrainModel = volumeCalculationHelper.checks.isPrismVolumeTowardsTerrainModel(
    calculationMethod,
    selectedTarget,
    selectedSource,
  );
  const isPrismVolumePointCloud = volumeCalculationHelper.checks.isPrismVolumeTowardsPointCloud(
    calculationMethod,
    selectedTarget,
    selectedSource,
  );
  const isPrismWithBoundary = volumeCalculationHelper.checks.isPrismWithBoundary(
    boundaryCalculationType,
    calculationMethod,
  );
  const isPrismIndexLimitExceeded = volumeCalculationHelper.checks.isPrismIndexLimitExceeded(
    calculationMethod,
    selectedTarget,
    selectedSource,
  );
  const isBoundarySelectionValid = volumeCalculationHelper.checks.isBoundarySelectionValid(
    boundaryCalculationType,
    activeTool,
    polygonVolumeIsValid,
    selectedBoundaryAsset,
  );

  const targetSuggestions = useMemo(
    () =>
      targetVolumeOptions
        ? targetVolumeOptions.map((option) => ({
            name: option.displayName,
            id: option.id,
          }))
        : [],
    [targetVolumeOptions],
  );

  const sourceSuggestions = useMemo(
    () =>
      sourceVolumeOptions
        ? sourceVolumeOptions.map((option) => ({
            name: option.displayName,
            id: option.id,
          }))
        : [],
    [sourceVolumeOptions],
  );

  const handleTargetSuggestionSelected = useCallback(
    async (suggestion: { name: string; id: string }) => {
      const selectedVolumeOption = volumeCalculationHelper.findVolumeCalculationOption(
        targetVolumeOptions,
        suggestion.id,
      );

      const selectedAsset = await volumeCalculationHelper.loadVolumeCalculationOption(
        selectedVolumeOption,
        setSelectedTarget,
      );
      activeTool.resetTargetForVolumeBoundaryPolygon(selectedAsset?.asset);
    },
    [targetVolumeOptions, activeTool],
  );

  const handleSourceSuggestionSelected = useCallback(
    async (suggestion: { name: string; id: string }) => {
      const selectedVolumeOption = volumeCalculationHelper.findVolumeCalculationOption(
        sourceVolumeOptions,
        suggestion.id,
      );

      await volumeCalculationHelper.loadVolumeCalculationOption(
        selectedVolumeOption,
        setSelectedSource,
      );
    },
    [sourceVolumeOptions],
  );

  const errors = [
    !isNameValid && t('validation.nameInvalid', { ns: 'skyviewVolumeCalculation' }),
    !isBoundarySelectionValid &&
      t('validation.boundarySelectionInvalid', { ns: 'skyviewVolumeCalculation' }),
    areAssetsSame && (
      <Trans
        components={{ bold: <strong /> }}
        i18nKey="validation.sameObjectsSelected"
        ns="skyviewVolumeCalculation"
      />
    ),
    isPrismVolumeTerrainModel && (
      <Trans
        components={{ bold: <strong /> }}
        i18nKey="validation.prismToTerrainModelNotSupported"
        ns="skyviewVolumeCalculation"
      />
    ),
    isPrismVolumePointCloud && (
      <Trans
        components={{ bold: <strong /> }}
        i18nKey="validation.prismToPointCloudNotSupported"
        ns="skyviewVolumeCalculation"
      />
    ),
    isPrismWithBoundary && (
      <Trans
        components={{ bold: <strong /> }}
        i18nKey="validation.prismWithBoundary"
        ns="skyviewVolumeCalculation"
      />
    ),
    isPrismIndexLimitExceeded &&
      t('validation.prismIndexLimitExceeded', { ns: 'skyviewVolumeCalculation' }),
  ].filter(Boolean);

  return (
    <Component spacing={0}>
      <RightPanelHeader
        icon={['fal', 'calculator']}
        name={t('title', { ns: 'skyviewVolumeCalculation' })}
      />

      <RightPanelContent useTopPadding={true}>
        <LabelledContainer text={t('name', { ns: 'skyviewVolumeCalculation' })}>
          {(formElementId) => (
            <TextBox
              id={formElementId()}
              value={volumeName}
              onChange={(e) => setVolumeName(e.target.value)}
            />
          )}
        </LabelledContainer>

        <CollapsableSectionGroup>
          <CollapsableSection
            expanded={expanded.methodSection}
            title={t('section.method.title', { ns: 'skyviewVolumeCalculation' })}
            titleUpperCase={true}
            tooltip={{
              text: (
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="section.method.tooltip"
                  ns="skyviewVolumeCalculation"
                />
              ),
              title: t('section.method.title', { ns: 'skyviewVolumeCalculation' }) as string,
            }}
            onSetExpanded={() =>
              setExpanded({ ...expanded, methodSection: !expanded.methodSection })
            }
          >
            <OptionLabels
              active={calculationMethod}
              itemRows={[
                [
                  {
                    title: t('section.method.itemCellGrid.text', {
                      ns: 'skyviewVolumeCalculation',
                    }),
                    value: 'cell_grid',
                    onSelect: () => setCalculationMethod('cell_grid'),
                    tooltipText: (
                      <Trans
                        components={{ br: <br />, bold: <strong /> }}
                        i18nKey="section.method.itemCellGrid.tooltip"
                        ns="skyviewVolumeCalculation"
                      />
                    ),
                  },
                  {
                    title: t('section.method.itemPrism.text', { ns: 'skyviewVolumeCalculation' }),
                    value: 'prism',
                    onSelect: () => setCalculationMethod('prism'),
                    tooltipText: (
                      <Trans
                        components={{ br: <br />, bold: <strong /> }}
                        i18nKey="section.method.itemPrism.tooltip"
                        ns="skyviewVolumeCalculation"
                      />
                    ),
                  },
                ],
              ]}
            />
          </CollapsableSection>

          <CollapsableSection
            expanded={expanded.targetAreaSection}
            title={t('common.targetSurface', { ns: 'skyviewVolumeCalculation' })}
            titleUpperCase={true}
            tooltip={{
              text: t('section.targetTooltip', { ns: 'skyviewVolumeCalculation' }),
              title: t('common.targetSurface', { ns: 'skyviewVolumeCalculation' }),
            }}
            onSetExpanded={() =>
              setExpanded({ ...expanded, targetAreaSection: !expanded.targetAreaSection })
            }
          >
            <Stack direction="column" spacing={0.8}>
              <DropdownSuggestion
                placeholder={t('search', { ns: 'common' })}
                portalToBody={true}
                suggestions={targetSuggestions}
                value={selectedTarget?.id}
                width={'100%'}
                onSuggestionSelected={handleTargetSuggestionSelected}
              />
              <VolumeInfoBlock
                selectedOption={selectedTarget}
                selectedOptionInfo={selectedTargetInfo}
                onUpdate={setSelectedTargetInfo}
              />
            </Stack>
          </CollapsableSection>

          <CollapsableSection
            expanded={expanded.sourceAreaSection}
            title={t('common.sourceSurface', { ns: 'skyviewVolumeCalculation' })}
            titleUpperCase={true}
            tooltip={{
              text: t('section.sourceTooltip', { ns: 'skyviewVolumeCalculation' }),
              title: t('common.sourceSurface', { ns: 'skyviewVolumeCalculation' }),
            }}
            onSetExpanded={() =>
              setExpanded({ ...expanded, sourceAreaSection: !expanded.sourceAreaSection })
            }
          >
            <Stack direction="column" spacing={0.8}>
              <DropdownSuggestion
                placeholder={t('search', { ns: 'common' })}
                portalToBody={true}
                suggestions={sourceSuggestions}
                value={selectedSource?.id}
                width={'100%'}
                onSuggestionSelected={handleSourceSuggestionSelected}
              />
              <VolumeInfoBlock
                selectedOption={selectedSource}
                selectedOptionInfo={selectedSourceInfo}
                onUpdate={setSelectedSourceInfo}
              />
            </Stack>
          </CollapsableSection>

          <CollapsableSection
            expanded={expanded.delimitSection}
            noBorder={true}
            title={t('section.boundary', { ns: 'skyviewVolumeCalculation' })}
            titleUpperCase={true}
            onSetExpanded={() =>
              setExpanded({ ...expanded, delimitSection: !expanded.delimitSection })
            }
          >
            <BoundaryBlock
              boundaryCalculationType={boundaryCalculationType}
              selectedBoundaryAsset={selectedBoundaryAsset}
              selectedTarget={selectedTarget}
              selectedTargetInfo={selectedTargetInfo}
              onBoundaryAssetChanged={setSelectedBoundaryAsset}
              onBoundaryCalculationChanged={setBoundaryCalculationType}
            />
          </CollapsableSection>
        </CollapsableSectionGroup>

        {errors.length > 0 && (
          <InfoBox color="red">
            <List items={errors} marker="disc" useMarkerForSingleItem={false} />
          </InfoBox>
        )}
      </RightPanelContent>

      <RightPanelFooter>
        <Button
          color={'primary'}
          disabled={
            !(
              isNameValid &&
              isBoundarySelectionValid &&
              isSourceAssetValid &&
              isTargetAssetValid &&
              !areAssetsSame &&
              !isPrismVolumeTerrainModel &&
              !isPrismVolumePointCloud &&
              !isPrismWithBoundary &&
              !isPrismIndexLimitExceeded
            )
          }
          variant={'contained'}
          width="100%"
          onClick={startCalculation}
        >
          {t('startButton', { ns: 'skyviewVolumeCalculation' })}
        </Button>
      </RightPanelFooter>
    </Component>
  );
};

const Component = styled(Stack)`
  height: 100%;
`;
